import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { LogIn, ArrowRight, Building2, Users } from 'lucide-react';

const XchangeLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Warm Premium Palette - Matching site theme
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4D8B86',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgPage: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    textOnDark: '#FFFFFF',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textMain: '#2C1810',
    borderWarm: '#E8E0D8',
    success: '#4D9078',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/xchange/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('xchange_token', data.token);
        localStorage.setItem('xchange_user', JSON.stringify(data.user));
        if (data.user.role === 'corporate') navigate('/saas/sponsor/dashboard');
        else if (data.user.role === 'ngo') navigate('/saas/agency/dashboard');
        else if (data.user.role === 'admin') navigate('/tmg-admin-console-x7k9');
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgPage }}>
      <Header />
      
      <div className="pt-28 pb-16 px-6 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div 
            className="rounded-[20px] p-8 md:p-10"
            style={{ 
              backgroundColor: colors.bgCard,
              boxShadow: '0 10px 40px rgba(44, 24, 16, 0.08)',
              border: `1px solid ${colors.borderWarm}`
            }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div 
                className="w-16 h-16 rounded-[14px] mx-auto mb-5 flex items-center justify-center"
                style={{ backgroundColor: colors.bgSage }}
              >
                <LogIn className="w-7 h-7" style={{ color: colors.primaryTeal }} />
              </div>
              <h1 
                className="text-2xl font-bold mb-2"
                style={{ color: colors.textMain }}
              >
                Welcome Back
              </h1>
              <p 
                className="text-sm"
                style={{ color: colors.textSecondary }}
              >
                Sign in to access your CSR Exchange dashboard
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.textMain }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-[10px] transition-all duration-200 outline-none"
                  style={{ 
                    backgroundColor: colors.bgSage,
                    border: '1px solid transparent',
                    color: colors.textMain
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  placeholder="you@company.com"
                  data-testid="login-email"
                />
              </div>

              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.textMain }}
                >
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-[10px] transition-all duration-200 outline-none"
                  style={{ 
                    backgroundColor: colors.bgSage,
                    border: '1px solid transparent',
                    color: colors.textMain
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>

              {error && (
                <div 
                  className="p-4 rounded-[10px] text-sm"
                  style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626' }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-[12px] font-semibold transition-all duration-300 disabled:opacity-50"
                style={{ 
                  backgroundColor: colors.primaryTeal,
                  color: colors.textOnDark,
                  boxShadow: '0 4px 14px rgba(61, 107, 103, 0.3)'
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = colors.primaryTealLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primaryTeal; }}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-4 my-8">
              <div className="flex-1 h-px" style={{ backgroundColor: colors.borderWarm }} />
              <span className="text-sm" style={{ color: colors.textTertiary }}>New to CSR Exchange?</span>
              <div className="flex-1 h-px" style={{ backgroundColor: colors.borderWarm }} />
            </div>

            {/* Register Options */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/xchange/corporate/register')}
                className="flex flex-col items-center gap-2 p-4 rounded-[12px] transition-all duration-200"
                style={{ backgroundColor: colors.bgSage }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(61, 107, 103, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid="register-corporate-link"
              >
                <Building2 className="w-6 h-6" style={{ color: colors.primaryTeal }} />
                <span className="text-sm font-medium" style={{ color: colors.textMain }}>Corporate</span>
              </button>
              <button
                onClick={() => navigate('/xchange/ngo/register')}
                className="flex flex-col items-center gap-2 p-4 rounded-[12px] transition-all duration-200"
                style={{ backgroundColor: colors.bgSage }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(61, 107, 103, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid="register-ngo-link"
              >
                <Users className="w-6 h-6" style={{ color: colors.primaryTeal }} />
                <span className="text-sm font-medium" style={{ color: colors.textMain }}>NGO</span>
              </button>
            </div>
          </div>

          {/* Demo Credentials */}
          <div 
            className="mt-6 p-5 rounded-[14px] text-center"
            style={{ backgroundColor: colors.bgSage }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: colors.primaryTeal }}>Demo Credentials</p>
            <p className="text-xs" style={{ color: colors.textSecondary }}>
              Corporate: rahul@techcorp.in / demo123<br />
              NGO: anita@wef.org.in / demo123
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default XchangeLogin;
